"use client";

import type { JSX } from "react";
import { Icon, Link, Text, View } from "@/components";

/** Badge de estado de la tarjeta (inscripción, aprobación, etc.). */
export interface CourseCardBadge {
  text: string;
  tone: "success" | "info" | "warning";
}

export interface CourseCardProps {
  /** Título del curso. */
  title: string;
  /** Destino del CTA principal. */
  href: string;
  /** Etiqueta del CTA principal (ya traducida). */
  cta: string;
  /** Chips descriptivos ya traducidos (p. ej. "Quiz por módulo", "Emite certificado"). */
  chips?: string[];
  /** Progreso 0-100; si se informa, se muestra la barra. */
  progress?: number;
  /** Badge de estado (p. ej. "En curso" / "Aprobado"). */
  badge?: CourseCardBadge;
  /** Texto secundario (p. ej. cantidad de módulos o fecha de inscripción). */
  detail?: string;
  /** CTA secundario opcional (destino + etiqueta ya traducida), de menor jerarquía visual. */
  secondary?: { href: string; cta: string };
}

const BADGE_TONES: Record<CourseCardBadge["tone"], string> = {
  success:
    "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300",
  info: "bg-muted text-muted-foreground",
  warning:
    "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
};

/**
 * @name CourseCard
 * @description Tarjeta de curso para el catálogo y "Mi aprendizaje".
 * Muestra título, chips derivados de las banderas del curso, un badge de estado,
 * la barra de progreso (si aplica) y un CTA principal, con un CTA secundario
 * opcional (p. ej. "Ver mi certificado" + "Repasar contenido"). Toda la información
 * refleja el estado que devuelve el servidor; la tarjeta no calcula reglas de avance.
 *
 * @param {CourseCardProps} props - Datos ya resueltos/traducidos por la página.
 * @returns {JSX.Element} La tarjeta renderizada.
 */
const CourseCard = ({
  title,
  href,
  cta,
  chips = [],
  progress,
  badge,
  detail,
  secondary
}: CourseCardProps): JSX.Element => (
  <View className="box flex flex-col gap-4 p-5 xl:p-6 rounded-2xl border border-border shadow-sm hover:shadow-md transition-shadow">
    <View className="flex items-start justify-between gap-3">
      <Text type="h3" className="text-lg font-semibold text-foreground">
        {title}
      </Text>
      {badge && (
        <Text
          type="span"
          className={`shrink-0 px-3 py-1 rounded-full text-xs font-medium ${BADGE_TONES[badge.tone]}`}
        >
          {badge.text}
        </Text>
      )}
    </View>

    {chips.length > 0 && (
      <View className="flex flex-wrap gap-2">
        {chips.map((chip) => (
          <Text
            key={chip}
            type="span"
            className="px-2.5 py-0.5 rounded-full text-xs bg-primary/10 text-primary"
          >
            {chip}
          </Text>
        ))}
      </View>
    )}

    {typeof progress === "number" && (
      <View className="flex items-center gap-3">
        <View className="flex-1 h-2 rounded-full bg-primary/15 overflow-hidden">
          <View
            className="h-full rounded-full bg-primary transition-all duration-500"
            style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
          />
        </View>
        <Text type="span" className="text-xs font-medium text-muted-foreground">
          {progress}%
        </Text>
      </View>
    )}

    {detail && <Text className="text-sm text-muted-foreground">{detail}</Text>}

    <View className="mt-auto pt-2 flex flex-wrap items-center gap-x-5 gap-y-2">
      <Link
        href={href}
        className="inline-flex items-center gap-2 min-h-11 sm:min-h-0 text-sm font-medium text-primary hover:underline"
      >
        {cta}
        <Icon name="fa-solid fa-arrow-right" className="text-xs!" />
      </Link>
      {secondary && (
        <Link
          href={secondary.href}
          className="inline-flex items-center gap-2 min-h-11 sm:min-h-0 text-sm font-medium text-muted-foreground hover:text-foreground hover:underline"
        >
          {secondary.cta}
        </Link>
      )}
    </View>
  </View>
);

export default CourseCard;
